import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  IconButton,
  Text,
  Textarea,
  Tooltip,
  VStack,
  HStack,
  Flex,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  AlertTriangle,
  ChevronLeft,
  FileText,
  ListChecks,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useReviewerCampaign } from '../hooks/useReviewerCampaign';
import { useBulkReviewerDecisions } from '../hooks/useBulkReviewerDecisions';
import { useAiComplianceReview } from '../hooks/useAiComplianceReview';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ComplianceSummaryStrip from '../components/campaigns/compliance/ComplianceSummaryStrip';
import BulkComplianceModal from '../components/campaigns/compliance/BulkComplianceModal';
import AiComplianceReviewModal from '../components/campaigns/compliance/AiComplianceReviewModal';
import ReviewerCampaignTable from '../components/campaigns/table/ReviewerCampaignTable';
import {
  COMPLIANCE_FILTERS,
  EMPTY_COMPLIANCE_SUMMARY,
  classifyComplianceForSummary,
  matchesComplianceFilter,
} from '../utils/complianceFilters';

/**
 * ReviewerCampaignPage
 *
 * Landing page a reviewer reaches by clicking a card in the "Shared
 * with me" tab. Phase 3.2 ships the read-only table; Phase 3.3 will
 * layer a click-to-edit decision popover on top without restructuring
 * this page.
 *
 * Page layout is fixed (header + scrollable table below) so horizontal
 * overflow inside the table stays contained. We don't rely on page
 * scroll — the virtualized table has its own scroll contexts.
 */

const ReviewerErrorCard = ({ title, body, onBack }) => (
  <Flex align="center" justify="center" minH="60vh" px={4}>
    <Box
      maxW="md"
      w="100%"
      p={8}
      borderRadius="12px"
      border="1px solid rgba(255,255,255,0.10)"
      bg="#0f0f0f"
    >
      <VStack spacing={4} align="stretch">
        <HStack spacing={3} align="center">
          <Box
            p={2.5}
            borderRadius="8px"
            bg="rgba(230, 179, 92, 0.12)"
            border="1px solid rgba(230, 179, 92, 0.25)"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <AlertTriangle size={20} strokeWidth={1.75} color="#e6b35c" />
          </Box>
          <Heading
            size="md"
            color="#fafafa"
            fontWeight="600"
            letterSpacing="-0.01em"
          >
            {title}
          </Heading>
        </HStack>
        <Text
          fontSize="sm"
          color="rgba(255,255,255,0.65)"
          lineHeight="1.65"
        >
          {body}
        </Text>
        <Button
          onClick={onBack}
          bg="#fafafa"
          color="#0a0a0a"
          borderRadius="full"
          fontWeight="600"
          fontSize="sm"
          h="40px"
          _hover={{ opacity: 0.88 }}
        >
          Back to home
        </Button>
      </VStack>
    </Box>
  </Flex>
);

// ─────────────────────────────────────────────────────────────────────────────
// Campaign-wide verdict badge
// ─────────────────────────────────────────────────────────────────────────────

const VERDICT_STYLES = {
  approved: {
    bg: 'rgba(34,197,94,0.10)',
    border: 'rgba(34,197,94,0.28)',
    color: 'rgba(74,222,128,0.9)',
    label: 'Approved',
  },
  rejected: {
    bg: 'rgba(239,68,68,0.10)',
    border: 'rgba(239,68,68,0.28)',
    color: 'rgba(248,113,113,0.9)',
    label: 'Rejected',
  },
};

const VerdictBadge = ({ verdictStatus }) => {
  if (!verdictStatus) return null;
  const s = VERDICT_STYLES[verdictStatus];
  if (!s) return null;
  return (
    <Box
      px={2}
      py="2px"
      borderRadius="full"
      bg={s.bg}
      border={`1px solid ${s.border}`}
      flexShrink={0}
    >
      <Text
        fontSize="10px"
        fontWeight="700"
        color={s.color}
        letterSpacing="0.06em"
        textTransform="uppercase"
      >
        {s.label}
      </Text>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Verdict confirmation modal
// ─────────────────────────────────────────────────────────────────────────────

const VerdictModal = ({ isOpen, onClose, verdictType, campaignName, onConfirm, isSubmitting }) => {
  const [note, setNote] = useState('');
  const isApprove = verdictType === 'approved';

  useEffect(() => {
    if (isOpen) setNote('');
  }, [isOpen]);

  const accentColor = isApprove ? 'rgba(74,222,128,0.9)' : 'rgba(248,113,113,0.9)';
  const accentBg = isApprove ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)';
  const accentBorder = isApprove ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.28)';
  const btnBg = isApprove ? '#22c55e' : '#ef4444';
  const btnColor = '#fff';
  const actionLabel = isApprove ? 'Approve campaign' : 'Reject campaign';
  const description = isApprove
    ? 'The campaign builder will receive an email confirming your approval.'
    : 'The campaign builder will receive an email with your rejection and can revise and resubmit.';

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(3px)" />
      <ModalContent
        bg="#0f0f0f"
        border="1px solid rgba(255,255,255,0.10)"
        borderRadius="12px"
        color="#fafafa"
        mx={4}
      >
        <ModalHeader pb={2}>
          <VStack align="start" spacing={1}>
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg={accentBg}
              border={`1px solid ${accentBorder}`}
              alignSelf="flex-start"
            >
              <Text fontSize="10px" fontWeight="700" color={accentColor} letterSpacing="0.06em" textTransform="uppercase">
                {isApprove ? 'Approve' : 'Reject'}
              </Text>
            </Box>
            <Text fontSize="md" fontWeight="600" color="#fafafa">
              {actionLabel}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="rgba(255,255,255,0.6)" lineHeight="1.6">
              {description}
            </Text>
            {campaignName && (
              <Box px={3} py={2} borderRadius="8px" bg="rgba(255,255,255,0.04)" border="1px solid rgba(255,255,255,0.08)">
                <Text fontSize="xs" color="rgba(255,255,255,0.4)" mb={0.5}>Campaign</Text>
                <Text fontSize="sm" fontWeight="500" color="#fafafa" noOfLines={2}>{campaignName}</Text>
              </Box>
            )}
            <Box>
              <Text fontSize="xs" color="rgba(255,255,255,0.45)" mb={1.5} fontWeight="500">
                Note {isApprove ? '(optional)' : '(required)'}
              </Text>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={isApprove ? 'Add any comments for the campaign builder…' : 'Explain what needs to be changed…'}
                size="sm"
                rows={3}
                bg="rgba(255,255,255,0.04)"
                border="1px solid rgba(255,255,255,0.10)"
                borderRadius="8px"
                color="#fafafa"
                fontSize="sm"
                resize="vertical"
                _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                _focus={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' }}
                _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
              />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid rgba(255,255,255,0.08)" gap={2}>
          <Button
            size="sm"
            variant="ghost"
            color="rgba(255,255,255,0.5)"
            onClick={onClose}
            isDisabled={isSubmitting}
            borderRadius="full"
            _hover={{ color: 'rgba(255,255,255,0.85)', bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            bg={btnBg}
            color={btnColor}
            borderRadius="full"
            fontWeight="600"
            fontSize="sm"
            h="32px"
            px={4}
            isLoading={isSubmitting}
            isDisabled={!isApprove && !note.trim()}
            onClick={() => onConfirm(note.trim() || null)}
            _hover={{ opacity: 0.88 }}
            _focus={{ boxShadow: 'none' }}
          >
            {actionLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const EMPTY_REVIEWER_ITEMS = Object.freeze([]);

const getReviewerCompliance = (contact, draft) =>
  contact?.drafts?.[draft.config_id]?.compliance || null;

const buildReviewerComplianceSummary = (contacts, drafts) => {
  if (!contacts.length || !drafts.length) return EMPTY_COMPLIANCE_SUMMARY;

  const summary = { ...EMPTY_COMPLIANCE_SUMMARY };
  for (const contact of contacts) {
    for (const draft of drafts) {
      const bucket = classifyComplianceForSummary(getReviewerCompliance(contact, draft));
      summary[bucket] += 1;
      summary.total += 1;
    }
  }
  return summary;
};

const contactMatchesReviewerFilter = (contact, drafts, filter) => {
  if (filter === COMPLIANCE_FILTERS.ALL) return true;
  return drafts.some((draft) =>
    matchesComplianceFilter(getReviewerCompliance(contact, draft), filter),
  );
};

const contactMatchesStatusColumnFilters = (contact, statusColumnFilters) => {
  if (!statusColumnFilters) return true;
  const entries = Object.entries(statusColumnFilters);
  if (entries.length === 0) return true;
  for (const [configId, filter] of entries) {
    if (!filter || filter === COMPLIANCE_FILTERS.ALL) continue;
    const decision = contact?.drafts?.[configId]?.compliance || null;
    if (!matchesComplianceFilter(decision, filter)) return false;
  }
  return true;
};

const ReviewerCampaignPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading, error } = useReviewerCampaign(campaignId);
  const [complianceFilter, setComplianceFilter] = useState(COMPLIANCE_FILTERS.ALL);
  const [statusColumnFilters, setStatusColumnFilters] = useState(() => ({}));
  const bulkModal = useDisclosure();
  const aiModal = useDisclosure();
  const { apply: applyBulk, isBulkInFlight } = useBulkReviewerDecisions(campaignId);

  const [verdictModal, setVerdictModal] = useState(null); // 'approved' | 'rejected' | null
  const [isSubmittingVerdict, setIsSubmittingVerdict] = useState(false);
  const [localVerdict, setLocalVerdict] = useState(null);
  const [retailPdfLoading, setRetailPdfLoading] = useState(false);

  // If the server clears the verdict (advisor re-submitted the campaign for
  // review), drop the optimistic local state so the UI doesn't stay stuck.
  const serverVerdict = data?.verdict_status ?? null;
  const currentVerdict = serverVerdict === null ? null : (localVerdict ?? serverVerdict);

  const handleVerdictConfirm = useCallback(async (note) => {
    if (!verdictModal || !campaignId) return;
    setIsSubmittingVerdict(true);
    try {
      await api.post(`/api/reviews/campaigns/${campaignId}/verdict`, {
        status: verdictModal,
        note,
      });
      setLocalVerdict(verdictModal);
      queryClient.invalidateQueries({ queryKey: ['reviewerCampaign', user?.id, campaignId] });
      queryClient.invalidateQueries({ queryKey: ['sharedCampaigns', user?.id] });
      toast({
        title: verdictModal === 'approved' ? 'Campaign approved' : 'Campaign rejected',
        description: 'The campaign builder has been notified by email.',
        status: verdictModal === 'approved' ? 'success' : 'warning',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
      setVerdictModal(null);
    } catch (err) {
      toast({
        title: 'Could not submit verdict',
        description: err?.response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsSubmittingVerdict(false);
    }
  }, [verdictModal, campaignId, queryClient, user?.id, toast]);

  const downloadRetailCommsPdf = useCallback(async () => {
    if (!campaignId || !data || data.verdict_status !== 'approved') return;
    setRetailPdfLoading(true);
    try {
      const res = await api.get(
        `/api/reviews/campaigns/${campaignId}/retail-communications-pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'];
      let fname = `gillyreach-retail-comms-${campaignId.slice(0, 8)}.pdf`;
      if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m?.[1]) fname = m[1];
      }
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast({
        title: 'Retail communications pack downloaded',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (err) {
      const raw = err?.response?.data;
      let msg = 'Please try again.';
      if (raw instanceof Blob) {
        try {
          const parsed = JSON.parse(await raw.text());
          if (typeof parsed?.detail === 'string') msg = parsed.detail;
        } catch {
          msg = 'Download failed.';
        }
      } else if (typeof raw?.detail === 'string') {
        msg = raw.detail;
      }
      toast({
        title: 'Could not download PDF',
        description: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setRetailPdfLoading(false);
    }
  }, [campaignId, data, toast]);

  // Toast bridge for the AI review hook — kept inline so the hook
  // stays Chakra-agnostic and we don't drag a useToast dependency
  // into a hook that may run in non-page contexts later.
  const handleAiToast = useCallback(
    ({ type, title, description }) => {
      toast({
        title,
        description,
        status: type === 'error' ? 'error' : 'success',
        duration: type === 'error' ? 6000 : 4500,
        isClosable: true,
        position: 'bottom-right',
      });
    },
    [toast],
  );

  const {
    start: startAiReview,
    observeComplianceTail,
    isRunning: aiRunning,
    progress: aiProgress,
  } = useAiComplianceReview(campaignId, { onToast: handleAiToast });

  const backToHome = () => navigate('/?tab=reviews');
  const drafts = data?.drafts || EMPTY_REVIEWER_ITEMS;
  const contacts = data?.contacts || EMPTY_REVIEWER_ITEMS;

  const draftIdsKey = useMemo(
    () => drafts.map((d) => String(d.config_id)).sort().join(','),
    [drafts],
  );
  const [aiReviewSelectedConfigIds, setAiReviewSelectedConfigIds] = useState([]);

  useEffect(() => {
    setAiReviewSelectedConfigIds(drafts.map((d) => d.config_id));
    // draftIdsKey reflects which config_ids exist; `drafts` may be a new array
    // reference on each React Query tick with identical contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on draftIdsKey, not drafts ref
  }, [campaignId, draftIdsKey]);

  useEffect(() => {
    if (!aiModal.isOpen) return;
    setAiReviewSelectedConfigIds(drafts.map((d) => d.config_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiModal.isOpen, draftIdsKey]);

  const bulkDisabled =
    isBulkInFlight || drafts.length === 0 || contacts.length === 0;
  const bulkTooltip = isBulkInFlight
    ? 'Applying previous bulk action…'
    : drafts.length === 0
      ? 'No drafts on this campaign yet'
      : contacts.length === 0
        ? 'No contacts to review yet'
        : 'Review in bulk';

  const aiDisabled =
    aiRunning || drafts.length === 0 || contacts.length === 0;
  const aiTooltip = aiRunning
    ? 'AI review in progress…'
    : drafts.length === 0
      ? 'No drafts on this campaign yet'
      : contacts.length === 0
        ? 'No contacts to review yet'
        : 'Review with AI';

  const retailPdfDisabled = !data || data.verdict_status !== 'approved';
  const retailPdfTooltip = retailPdfDisabled
    ? 'Available after you approve this campaign'
    : 'Download retail communications pack (PDF)';

  const handleBulkComplete = useCallback(
    ({ applied, skipped, failed }) => {
      const parts = [];
      parts.push(
        `${applied.toLocaleString()} ${applied === 1 ? 'cell' : 'cells'} updated`,
      );
      if (skipped > 0) {
        parts.push(`${skipped.toLocaleString()} manual flags preserved`);
      }
      if (failed > 0) {
        parts.push(`${failed.toLocaleString()} need refresh`);
      }
      toast({
        title: failed > 0 ? 'Bulk applied with retries' : 'Bulk applied',
        description: parts.join(' · '),
        status: failed > 0 ? 'warning' : 'success',
        duration: 4500,
        isClosable: true,
        position: 'bottom-right',
      });
    },
    [toast],
  );

  useEffect(() => {
    setComplianceFilter(COMPLIANCE_FILTERS.ALL);
    setStatusColumnFilters({});
    aiModal.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset filters on route; disclosure fns stable
  }, [campaignId]);

  const handleStatusColumnFilterChange = useCallback((configId, nextFilter) => {
    setStatusColumnFilters((prev) => {
      const next = { ...prev };
      if (!nextFilter || nextFilter === COMPLIANCE_FILTERS.ALL) {
        delete next[configId];
        return next;
      }
      next[configId] = nextFilter;
      return next;
    });
  }, []);

  const reviewerComplianceSummary = useMemo(
    () => buildReviewerComplianceSummary(contacts, drafts),
    [contacts, drafts],
  );

  const filteredContacts = useMemo(
    () =>
      contacts.filter((contact) => {
        const matchesGlobal = contactMatchesReviewerFilter(contact, drafts, complianceFilter);
        if (!matchesGlobal) return false;
        return contactMatchesStatusColumnFilters(contact, statusColumnFilters);
      }),
    [contacts, drafts, complianceFilter, statusColumnFilters],
  );

  const isComplianceFilterActive = complianceFilter !== COMPLIANCE_FILTERS.ALL;
  const hasStatusColumnFilters = Object.keys(statusColumnFilters).length > 0;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Spinner size="md" color="rgba(255,255,255,0.55)" thickness="2px" />
      </Flex>
    );
  }

  if (error) {
    const statusCode = error?.response?.status;
    const title =
      statusCode === 404
        ? "We couldn't load this review"
        : 'Something went wrong';
    const body =
      statusCode === 404
        ? "This shared campaign isn't visible to your account right now. It may have been revoked, or your invitation may have been issued for a different account."
        : 'We ran into an unexpected problem loading this campaign. Try again in a minute, or head back to your home page.';
    return <ReviewerErrorCard title={title} body={body} onBack={backToHome} />;
  }

  if (!data) {
    return (
      <ReviewerErrorCard
        title="We couldn't load this review"
        body="This shared campaign isn't available right now. Head back to your home page to see your active reviews."
        onBack={backToHome}
      />
    );
  }

  // Notion/Anthropic-style meta strip: the campaign name lives in the
  // topbar breadcrumb (see Header.jsx) so this row only carries the
  // lightweight context a reviewer needs at a glance — who shared it,
  // and how many contacts they're reviewing. Everything else is chrome
  // we don't want on a read-heavy surface.
  const ownerLine = data.owner_email
    ? `${data.owner_display_name} · ${data.owner_email}`
    : data.owner_display_name;
  const contactCountLabel = `${data.total_contacts} ${
    data.total_contacts === 1 ? 'contact' : 'contacts'
  }${data.truncated ? ' (showing first 1,000)' : ''}`;

  return (
    <Flex direction="column" h="calc(100vh - 64px)" minH="0">
      {/* Compact toolbar — mirrors the advisor CampaignPage toolbar so
          back-navigation sits in the same spot across both surfaces.
          Keeping only the back button for now; right-hand action icons
          are intentionally left out until we decide what (if anything)
          a reviewer should see there. */}
      <Box
        flexShrink={0}
        pl={3}
        pr={2}
        py={1}
        borderBottom="1px solid"
        borderColor="rgba(255,255,255,0.08)"
        bg="#0a0a0a"
      >
        <HStack justify="space-between" align="center" spacing={1}>
          <HStack spacing={1}>
            <Button
              leftIcon={<ChevronLeft size={20} strokeWidth={1.5} />}
              size="xs"
              variant="ghost"
              color="gray.400"
              fontSize="xs"
              height="28px"
              minW="auto"
              px={2}
              _hover={{
                color: 'rgba(255,255,255,0.7)',
                bg: 'transparent',
                boxShadow: 'none',
              }}
              _active={{ bg: 'transparent', boxShadow: 'none' }}
              onClick={backToHome}
              aria-label="Back to shared campaigns"
            />
            <VerdictBadge verdictStatus={currentVerdict} />
          </HStack>

          <HStack spacing={1.5} align="center">
            {/* Campaign-wide Approve / Reject */}
            <Tooltip
              label={currentVerdict === 'approved' ? 'Already approved — click to change' : 'Approve this campaign'}
              placement="bottom"
              openDelay={300}
              bg="rgba(20,20,20,0.95)"
              color="rgba(255,255,255,0.85)"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
            >
              <IconButton
                icon={<ThumbsUp size={14} strokeWidth={1.75} />}
                aria-label="Approve campaign"
                size="xs"
                variant="ghost"
                color={currentVerdict === 'approved' ? 'rgba(74,222,128,0.85)' : 'rgba(255,255,255,0.45)'}
                height="28px"
                minW="28px"
                borderRadius="6px"
                onClick={() => setVerdictModal('approved')}
                _hover={{ color: 'rgba(74,222,128,0.9)', bg: 'rgba(34,197,94,0.08)' }}
                _active={{ bg: 'rgba(34,197,94,0.12)' }}
                _focus={{ boxShadow: 'none' }}
              />
            </Tooltip>
            <Tooltip
              label={currentVerdict === 'rejected' ? 'Already rejected — click to change' : 'Reject this campaign'}
              placement="bottom"
              openDelay={300}
              bg="rgba(20,20,20,0.95)"
              color="rgba(255,255,255,0.85)"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
            >
              <IconButton
                icon={<ThumbsDown size={14} strokeWidth={1.75} />}
                aria-label="Reject campaign"
                size="xs"
                variant="ghost"
                color={currentVerdict === 'rejected' ? 'rgba(248,113,113,0.85)' : 'rgba(255,255,255,0.45)'}
                height="28px"
                minW="28px"
                borderRadius="6px"
                onClick={() => setVerdictModal('rejected')}
                _hover={{ color: 'rgba(248,113,113,0.9)', bg: 'rgba(239,68,68,0.08)' }}
                _active={{ bg: 'rgba(239,68,68,0.12)' }}
                _focus={{ boxShadow: 'none' }}
              />
            </Tooltip>

            <Box w="1px" h="16px" bg="rgba(255,255,255,0.08)" mx={0.5} />

            {aiRunning && (
              <HStack
                spacing={1.5}
                px={2}
                py="2px"
                borderRadius="full"
                bg="rgba(167,139,250,0.10)"
                border="1px solid rgba(167,139,250,0.28)"
              >
                <Sparkles size={11} strokeWidth={2} color="#A78BFA" />
                <Text fontSize="11px" color="rgba(255,255,255,0.75)" fontWeight="500">
                  AI reviewing… {Math.min(99, Math.max(1, aiProgress))}%
                </Text>
              </HStack>
            )}
            <Tooltip
              label={aiTooltip}
              placement="bottom-end"
              openDelay={250}
              hasArrow
              bg="rgba(20,20,20,0.95)"
              color="rgba(255,255,255,0.85)"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
            >
              <IconButton
                icon={<Sparkles size={15} strokeWidth={1.75} />}
                aria-label="Review with AI"
                size="xs"
                variant="ghost"
                color="rgba(255,255,255,0.55)"
                height="28px"
                minW="28px"
                borderRadius="6px"
                isDisabled={aiDisabled}
                onClick={aiModal.onOpen}
                _hover={{
                  color: 'rgba(255,255,255,0.9)',
                  bg: 'rgba(255,255,255,0.06)',
                }}
                _active={{ bg: 'rgba(255,255,255,0.08)' }}
                _disabled={{
                  color: 'rgba(255,255,255,0.2)',
                  cursor: 'not-allowed',
                  _hover: { bg: 'transparent' },
                }}
              />
            </Tooltip>
            <Tooltip
              label={bulkTooltip}
              placement="bottom-end"
              openDelay={250}
              hasArrow
              bg="rgba(20,20,20,0.95)"
              color="rgba(255,255,255,0.85)"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
            >
              <IconButton
                icon={<ListChecks size={15} strokeWidth={1.75} />}
                aria-label="Review in bulk"
                size="xs"
                variant="ghost"
                color="rgba(255,255,255,0.55)"
                height="28px"
                minW="28px"
                borderRadius="6px"
                isDisabled={bulkDisabled}
                onClick={bulkModal.onOpen}
                _hover={{
                  color: 'rgba(255,255,255,0.9)',
                  bg: 'rgba(255,255,255,0.06)',
                }}
                _active={{ bg: 'rgba(255,255,255,0.08)' }}
                _disabled={{
                  color: 'rgba(255,255,255,0.2)',
                  cursor: 'not-allowed',
                  _hover: { bg: 'transparent' },
                }}
              />
            </Tooltip>
            <Tooltip
              label={retailPdfTooltip}
              placement="bottom-end"
              openDelay={250}
              hasArrow
              bg="rgba(20,20,20,0.95)"
              color="rgba(255,255,255,0.85)"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
            >
              <IconButton
                icon={<FileText size={15} strokeWidth={1.75} />}
                aria-label="Download retail communications PDF"
                size="xs"
                variant="ghost"
                color="rgba(255,255,255,0.55)"
                height="28px"
                minW="28px"
                borderRadius="6px"
                isDisabled={retailPdfDisabled}
                isLoading={retailPdfLoading}
                onClick={downloadRetailCommsPdf}
                _hover={{
                  color: 'rgba(255,255,255,0.9)',
                  bg: 'rgba(255,255,255,0.06)',
                }}
                _active={{ bg: 'rgba(255,255,255,0.08)' }}
                _disabled={{
                  color: 'rgba(255,255,255,0.2)',
                  cursor: 'not-allowed',
                  _hover: { bg: 'transparent' },
                }}
              />
            </Tooltip>
          </HStack>
        </HStack>
      </Box>

      <Box
        flexShrink={0}
        px={{ base: 4, md: 6 }}
        pt={3}
        pb={3}
        bg="#0a0a0a"
      >
        <HStack
          spacing={2}
          fontSize="xs"
          color="rgba(255,255,255,0.55)"
          flexWrap="wrap"
          rowGap={1}
        >
          <Text>
            Shared by{' '}
            <Text as="span" color="rgba(255,255,255,0.85)" fontWeight="500">
              {ownerLine}
            </Text>
          </Text>
          <Text color="rgba(255,255,255,0.25)">·</Text>
          <Text>{contactCountLabel}</Text>
        </HStack>
      </Box>

      <BulkComplianceModal
        isOpen={bulkModal.isOpen}
        onClose={bulkModal.onClose}
        drafts={drafts}
        contacts={contacts}
        onApply={applyBulk}
        onComplete={handleBulkComplete}
      />

      <VerdictModal
        isOpen={verdictModal !== null}
        onClose={() => !isSubmittingVerdict && setVerdictModal(null)}
        verdictType={verdictModal}
        campaignName={data?.campaign_name}
        onConfirm={handleVerdictConfirm}
        isSubmitting={isSubmittingVerdict}
      />

      <AiComplianceReviewModal
        isOpen={aiModal.isOpen}
        onClose={aiModal.onClose}
        drafts={drafts}
        contacts={contacts}
        selectedConfigIds={aiReviewSelectedConfigIds}
        onSelectedConfigIdsChange={setAiReviewSelectedConfigIds}
        onStart={startAiReview}
      />

      <Box flex={1} minH={0} px={{ base: 2, md: 4 }} pb={3}>
        <Box
          h="100%"
          borderRadius="10px"
          border="1px solid rgba(255,255,255,0.06)"
          overflow="hidden"
          bg="#0a0a0a"
        >
          <Flex direction="column" h="100%" minH="0">
            <ComplianceSummaryStrip
              isEnabled={drafts.length > 0}
              summary={reviewerComplianceSummary}
              filter={complianceFilter}
              onFilterChange={setComplianceFilter}
            />
            <Box flex={1} minH={0}>
              <ReviewerCampaignTable
                campaignId={data.campaign_id}
                drafts={drafts}
                contacts={filteredContacts}
                reviewerContactCount={contacts.length}
                onObserveComplianceTail={observeComplianceTail}
                contactRowsForColumnLayout={contacts}
                statusColumnFilters={statusColumnFilters}
                onStatusColumnFilterChange={handleStatusColumnFilterChange}
                emptyMessage={
                  (isComplianceFilterActive || hasStatusColumnFilters) && contacts.length > 0
                    ? 'No contacts match the selected compliance filter.'
                    : undefined
                }
              />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
};

export default ReviewerCampaignPage;
